/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {debounce} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import MuiTooltip, {TooltipProps} from '@material-ui/core/Tooltip';
import isNil from 'lodash.isnil';
import {useEventListener} from '@ion/core/src/hooks/useEventListener';
import {GenericObject} from '@ion/core/src/types/entities';
import {useIsPhone} from '@ion/dls/src/hooks/useMedia';
import {dlsAlt, dlsColor, dlsTypography} from '../../../src/theme/theme';

export const useNavigationStyle = makeStyles((theme) => ({
  tooltip : {
    ...dlsTypography('caption'),
    backgroundColor : dlsAlt('colors', 'tooltipBackground'),
    color           : dlsColor('white'),
    letterSpacing   : '1px',
    fontWeight      : 500,
    padding         : '8px',
    marginLeft      : '10px',
    fontSize        : '10px !important',
    lineHeight      : '20px !important',
    minWidth        : 120,
    textAlign       : 'left',
    fontFamily      : 'Neo Sans Std',
    textTransform   : 'uppercase',
    boxShadow       : '0px 3px 5px 0px #00000033, 0px 1px 18px 0px #0000001F, 0px 6px 10px 0px #00000024'

  },
  arrow : {
    color : dlsAlt('colors', 'tooltipBackground')
  }
}));

const useStyles = makeStyles((theme) => ({
  tooltip : {
    ...dlsTypography('caption'),
    backgroundColor : dlsAlt('colors', 'tooltipBackground'),
    color           : dlsColor('white'),
    letterSpacing   : '0.4px',
    fontWeight      : 400,
    padding         : '4px 8px',
    fontSize        : '12px !important',
    lineHeight      : '16px !important',
    minWidth        : 100,
    textAlign       : 'center'
  },
  arrow : {
    color : dlsAlt('colors', 'tooltipBackground')
  }
}));

export interface TooltipTypes extends TooltipProps {
  renderOnlyChildren?: boolean;
  isOpen?: boolean;
  externalClasses?: GenericObject;
};

/**
 * @Tooltip
 *
 * Defines the Tooltip component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 * import React from 'react';
 * import {Tooltip} from '@ion/dls/src/tooltip';
 * import DeleteIcon from '@material-ui/icons/Delete';
 * import IconButton from '@material-ui/core/IconButton';
 *
 * function MyComponent {
 *   return (
 *     <React.Fragment>
 *      <Tooltip title="Delete">
 *        <IconButton>
 *          <DeleteIcon />
 *        </IconButton>
 *      </Tooltip>
 *     </React.Fragment>
 *   );
 * }
 *
 * ```
 *
 */
const Tooltip = ({
  children,
  externalClasses,
  renderOnlyChildren = false,
  isOpen = false,
  ...props
}: TooltipTypes) => {
  const classes         = useStyles();
  const [open, setOpen] = useState(isOpen);
  const isPhone         = useIsPhone();
  const element         = useMemo(
    () => document.getElementById('base_layout_content'),
    [isPhone]
  );

  // Allows for external setting of open tooltip state
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  const onHandleScroll = useCallback(debounce((e) => {
    if (e && open) {
      setOpen(false);
    };
  }, 200), [open]);

  useEventListener('scroll', onHandleScroll, element);

  // invoke the method when the user mouse over or click the trigger point...
  const onOpen = function() {
    setOpen(!props.disableHoverListener);
  };

  // invoke the method when the user go away or click outside of trigger point...
  const onClose = function() {
    setOpen(false);
  };

  if (renderOnlyChildren || isNil(props?.title)) {
    return children;
  }

  return (
    <MuiTooltip open={open}
      classes={externalClasses ?? classes}
      onMouseMove={onOpen}
      onMouseOut={onClose}
      {...props}>
      {children}
    </MuiTooltip>
  );
};

export default Tooltip;
